import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Container, Form, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getApiBaseUrl } from '../../utils/apiConfig';
import OpdDynamicForm from './OpdDynamicForm';
import {
  computeAgeMonths,
  currentTimeHHMM,
  parseSchema,
  normalizeOpdTemplate,
  pickRecommendedTemplate,
  todayIsoDate,
  validateBeforeSubmit,
} from './utils/opdFormSchema';
import './OpdFormDemo.css';

const emptyPatient = () => ({
  patientName: '',
  patientAgeYears: '',
  patientAgeMonths: '',
  patientMobile: '',
  patientEmail: '',
  visitDate: todayIsoDate(),
  visitTime: currentTimeHHMM(),
});

function OpdFormDemo() {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [schema, setSchema] = useState({ sections: [] });
  const [formData, setFormData] = useState({});
  const [patient, setPatient] = useState(emptyPatient);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const ageMonths = useMemo(
    () => computeAgeMonths(patient.patientAgeYears, patient.patientAgeMonths),
    [patient.patientAgeYears, patient.patientAgeMonths]
  );

  const loadTemplates = useCallback(async (months) => {
    setLoadingTemplates(true);
    try {
      const params = {};
      if (Number.isFinite(months) && months >= 0) {
        params.ageMonths = months;
      }
      const response = await axios.get(`${getApiBaseUrl()}/opdform/templates`, { params });
      const raw = response.data?.data || [];
      const list = raw
        .map((row, index) => normalizeOpdTemplate(row, index))
        .filter(Boolean)
        .filter(
          (t, idx, arr) =>
            arr.findIndex((x) => String(x.OpdFormTemplateId) === String(t.OpdFormTemplateId)) === idx
        );
      setTemplates(list);
      const picked = pickRecommendedTemplate(list);
      if (picked) {
        setTemplateId(String(picked.OpdFormTemplateId));
        setSchema(parseSchema(picked.SchemaJson));
      } else {
        setTemplateId('');
        setSchema({ sections: [] });
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not load OPD templates. Run DB deploy script if this is a fresh environment.');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates(ageMonths);
  }, [ageMonths, loadTemplates]);

  useEffect(() => {
    if (!templateId || !templates.length) return;
    const match = templates.find((t) => String(t.OpdFormTemplateId) === String(templateId));
    if (match) {
      setSchema(parseSchema(match.SchemaJson));
    }
  }, [templateId, templates]);

  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    setPatient((prev) => ({ ...prev, [name]: value }));
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateBeforeSubmit(schema, formData, {
      patientName: patient.patientName,
      patientEmail: patient.patientEmail,
      templateId,
    });
    if (errors.length) {
      toast.warning(errors[0]);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        OpdFormTemplateId: Number(templateId),
        PatientName: patient.patientName.trim(),
        PatientAgeYears: patient.patientAgeYears === '' ? null : Number(patient.patientAgeYears),
        PatientAgeMonths: patient.patientAgeMonths === '' ? null : Number(patient.patientAgeMonths),
        PatientMobile: patient.patientMobile.trim(),
        PatientEmail: patient.patientEmail.trim(),
        VisitDate: patient.visitDate || null,
        VisitTime: patient.visitTime || null,
        FormDataJson: formData,
        UserId: 1,
      };
      const response = await axios.post(`${getApiBaseUrl()}/opdform/submissions/submit`, payload);
      const { message, data } = response.data || {};
      if (data?.emailSent) {
        toast.success(message || 'Submitted successfully.');
      } else {
        toast.info(message || 'Saved; email may not have been sent.');
      }
      setFormData({});
      setPatient(emptyPatient());
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Submit failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="opd-demo-page">
      <ToastContainer position="top-center" autoClose={5000} />
      <header className="opd-demo-header">
        <Container>
          <h1>OPD demo form</h1>
          <p className="mb-0 small opacity-75">Pediatric template · saves to database · emails PDF to patient</p>
        </Container>
      </header>

      <Container className="py-3">
        <Form onSubmit={handleSubmit}>
          <div className="card mb-3 shadow-sm">
            <div className="card-header fw-semibold">Patient</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      name="patientName"
                      value={patient.patientName}
                      onChange={handlePatientChange}
                      required
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-6 col-md-3">
                  <Form.Group>
                    <Form.Label>Age (years)</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      name="patientAgeYears"
                      value={patient.patientAgeYears}
                      onChange={handlePatientChange}
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-6 col-md-3">
                  <Form.Group>
                    <Form.Label>Age (months)</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={11}
                      name="patientAgeMonths"
                      value={patient.patientAgeMonths}
                      onChange={handlePatientChange}
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-12 col-md-4">
                  <Form.Group>
                    <Form.Label>Mobile</Form.Label>
                    <Form.Control
                      name="patientMobile"
                      value={patient.patientMobile}
                      onChange={handlePatientChange}
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-12 col-md-4">
                  <Form.Group>
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      name="patientEmail"
                      value={patient.patientEmail}
                      onChange={handlePatientChange}
                      required
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-6 col-md-2">
                  <Form.Group>
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="visitDate"
                      value={patient.visitDate}
                      onChange={handlePatientChange}
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-6 col-md-2">
                  <Form.Group>
                    <Form.Label>Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="visitTime"
                      value={patient.visitTime}
                      onChange={handlePatientChange}
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3 shadow-sm">
            <div className="card-header fw-semibold">Form template</div>
            <div className="card-body">
              {loadingTemplates ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" /> Loading templates…
                </div>
              ) : (
                <Form.Select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  style={{ fontSize: '16px' }}
                >
                  <option key="opd-template-placeholder" value="">
                    Select template…
                  </option>
                  {templates.map((t, index) => (
                    <option
                      key={`opd-template-${t.OpdFormTemplateId}-${t.TemplateKey}-${index}`}
                      value={String(t.OpdFormTemplateId)}
                    >
                      {`${t.TemplateName}${Number(t.IsRecommended) === 1 ? ' (recommended for age)' : ''} — ages ${t.AgeMinMonths}–${t.AgeMaxMonths} mo`}
                    </option>
                  ))}
                </Form.Select>
              )}
            </div>
          </div>

          {templateId ? (
            <OpdDynamicForm
              schema={schema}
              formData={formData}
              onFieldChange={handleFieldChange}
            />
          ) : null}

          <div className="opd-sticky-submit">
            <button
              type="submit"
              className="btn btn-primary w-100 w-md-auto px-4"
              disabled={submitting || !templateId}
            >
              {submitting ? 'Submitting…' : 'Submit & email PDF to patient'}
            </button>
          </div>
        </Form>
      </Container>
    </div>
  );
}

export default OpdFormDemo;
